import type {ReactNode} from 'react';
import React from 'react';
import classNames from 'classnames';

import {useCollapseToFit} from './useCollapseToFit';
import {useOverflowCount} from './useOverflowCount';

type CommonProps = {
  /** Bump when content width changes for a reason other than a row resize. */
  remeasureKey?: string | number;
  /** Extra classes for the row element (e.g. width behavior, padding, max-height). */
  className?: string;
  'data-testid'?: string;
};

export type ResponsiveRowSwapProps = CommonProps & {
  /** Binary swap between `expanded` and `collapsed` (the default mode). */
  mode?: 'swap';
  /** Always-visible content; never collapses. Rendered at the start of the row. */
  fixed: ReactNode;
  /**
   * Full representation of the collapsible region, shown when it fits on one line
   * beside `fixed`. Measured at its natural width, so give it an intrinsic size
   * rather than letting it flex-grow to fill the row.
   */
  expanded: ReactNode;
  /** Compact representation; shown when `expanded` would not fit on one line. */
  collapsed: ReactNode;
  /**
   * `'between'` (default) pushes the collapsible region to the end of the row;
   * `'start'` keeps it adjacent to `fixed`.
   */
  align?: 'between' | 'start';
};

export type ResponsiveRowOverflowProps<T> = CommonProps & {
  /** Show as many items as fit + an always-present trailing control. */
  mode: 'overflow';
  /** The full list; the row shows the leading prefix that fits. */
  items: T[];
  /** Render one item. Return a keyed element (e.g. `<Chip key={item.id} />`). */
  renderItem: (item: T, index: number) => ReactNode;
  /**
   * The always-present trailing control (e.g. a "Show all" menu), rendered right
   * after the last visible item. A function receives the hidden-item count.
   */
  overflow: ReactNode | ((hiddenCount: number) => ReactNode);
};

export type ResponsiveRowProps<T = unknown> =
  | ResponsiveRowSwapProps
  | ResponsiveRowOverflowProps<T>;

const SwapRow = ({
  fixed,
  expanded,
  collapsed,
  align = 'between',
  remeasureKey,
  className,
  'data-testid': dataTestId
}: ResponsiveRowSwapProps) => {
  const {
    rowRef,
    fixedRef,
    collapsibleRef,
    collapsed: isCollapsed
  } = useCollapseToFit({
    remeasureKey
  });

  return (
    <div
      ref={rowRef}
      data-testid={dataTestId}
      className={classNames(
        // `flex-wrap` is load-bearing: the collapsible wraps to a 2nd line when
        // it cannot fit, which is exactly how the hook detects "doesn't fit"
        // (offsetTop compare). `overflow-clip` + the pre-paint measurement keep
        // that transient 2nd line from ever painting. `min-w-0` lets the row
        // shrink inside flex/grid parents so the ResizeObserver sees real width.
        'min-w-0 relative flex w-full flex-wrap items-center gap-x2 overflow-clip',
        className
      )}
    >
      {/* `shrink-0` on both wrappers preserves natural widths, so the collapsible
          genuinely wraps (rather than silently shrinking) when it overflows —
          which is exactly what the offsetTop compare in useCollapseToFit reads. */}
      <div ref={fixedRef} className='flex shrink-0 items-center'>
        {fixed}
      </div>
      <div
        ref={collapsibleRef}
        className={classNames('flex shrink-0 items-center', {'ml-auto': align === 'between'})}
      >
        {isCollapsed ? collapsed : expanded}
      </div>
    </div>
  );
};

function OverflowRow<T>({
  items,
  renderItem,
  overflow,
  remeasureKey,
  className,
  'data-testid': dataTestId
}: ResponsiveRowOverflowProps<T>) {
  const {rowRef, visibleCount} = useOverflowCount({remeasureKey});
  const visibleItems = Number.isFinite(visibleCount) ? items.slice(0, visibleCount) : items;
  const hiddenCount = items.length - visibleItems.length;
  const control = typeof overflow === 'function' ? overflow(hiddenCount) : overflow;

  return (
    <div
      ref={rowRef}
      data-testid={dataTestId}
      className={classNames(
        // `flex-1` fills the space next to any leading content; `flex-wrap` +
        // `overflow-clip` + the pre-paint measurement contain the render-all pass
        // (add `max-h-*` for a hard single-line clip). `content-start` top-aligns
        // that transient extra line. The control is always the last child.
        'min-w-0 relative flex flex-1 flex-wrap content-start items-center gap-x1 overflow-clip',
        className
      )}
    >
      {visibleItems.map((item, index) => renderItem(item, index))}
      {control}
    </div>
  );
}

/**
 * A responsive row with two modes:
 *
 * - **`swap`** (default) — a binary swap: show `expanded` when it fits on one
 *   line beside a `fixed` region, else `collapsed`. Classic toolbar: `[tabs]`
 *   stay while `[search] [New]` collapse to icon buttons.
 * - **`overflow`** — show as many `items` as fit + an always-present `overflow`
 *   control (the count-overflow / "priority+" pattern). Classic case: filter
 *   chips overflowing into a "Show all" menu.
 *
 * Both measure real layout (not viewport breakpoints), so they react to the
 * row's own container width and work inside sidebars, split panes, modals, etc.
 * Measurement is pre-paint and the row is `overflow-clip`, so nothing flashes.
 *
 * Accessibility: content is mounted/unmounted as the row resizes, so give every
 * representation its own accessible name (icon-only controls need an
 * `aria-label`). The change is layout-driven and so intentionally silent (like
 * responsive CSS); this primitive is content-agnostic, so manage focus or
 * announcements within the content you pass in if it can hold focus while it
 * swaps.
 *
 * @example // swap
 * <ResponsiveRow
 *   fixed={<FilterTabs />}
 *   expanded={<><SearchInput /><Button>New assignment</Button></>}
 *   collapsed={<><SearchIconButton /><AddIconButton /></>}
 * />
 *
 * @example // overflow
 * <ResponsiveRow
 *   mode="overflow"
 *   items={chips}
 *   renderItem={(chip) => <Chip key={chip.id} label={chip.label} />}
 *   overflow={(hiddenCount) => <ShowAllMenu hidden={hiddenCount} />}
 * />
 */
export function ResponsiveRow<T = unknown>(props: ResponsiveRowProps<T>) {
  if (props.mode === 'overflow') {
    return <OverflowRow {...props} />;
  }
  return <SwapRow {...props} />;
}
